import { useState, useEffect } from 'react'
import {
    Plus,
    Search,
    X,
    Building2,
    FileText,
    Save,
    AlertCircle,
    Building,
    Grid3x3,
    List
} from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { formatCurrency } from '../utils/formatCurrency'
import { useModal } from '../contexts/ModalContext'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../hooks/useToast'
import { ToastContainer } from '../components/Toast'
import Button from '../components/Button'
import ConstrutorasCardView from '../components/ConstrutorasCardView'
import ConstrutorasListView from '../components/ConstrutorasListView'

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

// Estados brasileiros
const ESTADOS_BRASILEIROS = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' }
]

// Interfaces baseadas no schema
interface Construtora {
    id: string
    nome: string
    cnpj: string
    contato: string | null
    estado: string | null
    cidade: string | null
    created_at: string
    // Dados calculados
    imoveis_count?: number
    valor_total_imoveis?: number
}

export default function Construtoras() {
    const { user } = useAuth()
    const { showSuccess, toasts } = useToast()
    const [construtoras, setConstrutoras] = useState<Construtora[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingConstrutora, setEditingConstrutora] = useState<Construtora | null>(null)
    const [isViewMode, setIsViewMode] = useState(false)
    const [formData, setFormData] = useState({
        nome: '',
        cnpj: '',
        contato: '',
        estado: '',
        cidade: ''
    })
    const [error, setError] = useState('')
    const [cnpjError, setCnpjError] = useState('')
    const { setIsModalOpen: setGlobalModalOpen } = useModal()
    const [estados, setEstados] = useState(ESTADOS_BRASILEIROS)
    const [cidades, setCidades] = useState<string[]>([])
    const [loadingEstados, setLoadingEstados] = useState(false)
    const [loadingCidades, setLoadingCidades] = useState(false)
    // Carregar viewMode do localStorage ou usar 'cards' como padrão
    const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
        const saved = localStorage.getItem('construtoras_viewMode')
        return (saved === 'cards' || saved === 'list') ? saved : 'cards'
    })

    // Carregar dados
    useEffect(() => {
        loadConstrutoras()
        loadEstadosBrasil()
    }, [])

    // Salvar viewMode no localStorage quando mudar
    useEffect(() => {
        localStorage.setItem('construtoras_viewMode', viewMode)
    }, [viewMode])

    const loadConstrutoras = async () => {
        try {
            // Verificar se usuário está logado
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) {
                setError('Usuário não está logado')
                setLoading(false)
                return
            }

            // Carregar construtoras do corretor logado (RLS já filtra automaticamente)
            const { data: construtorasData, error: construtorasError } = await supabase
                .from('construtoras')
                .select(`
                    id,
                    nome,
                    cnpj,
                    contato_responsavel,
                    estado,
                    cidade,
                    created_at,
                    imoveis_count:imoveis(count),
                    imoveis(valor_venda)
                `)
                .order('created_at', { ascending: false })

            if (construtorasError) throw construtorasError

            // Processar dados com contagem e valores
            const construtorasWithCounts = (construtorasData || []).map(construtora => {
                const imoveis_count = construtora.imoveis_count?.[0]?.count || 0
                const valor_total_imoveis = construtora.imoveis?.reduce(
                    (acc: number, imovel: any) => acc + parseFloat(imovel.valor_venda?.toString() || '0'),
                    0
                ) || 0

                return {
                    id: construtora.id,
                    nome: construtora.nome,
                    cnpj: construtora.cnpj,
                    contato: construtora.contato_responsavel,
                    estado: construtora.estado,
                    cidade: construtora.cidade,
                    created_at: construtora.created_at,
                    imoveis_count,
                    valor_total_imoveis
                }
            })

            setConstrutoras(construtorasWithCounts)
        } catch (err: any) {
            console.error('Erro ao carregar construtoras:', err)
            console.error('Detalhes do erro:', {
                message: err.message,
                code: err.code,
                details: err.details,
                hint: err.hint
            })

            if (err.message?.includes('JWT')) {
                setError('Sessão expirada. Faça login novamente.')
            } else if (err.message?.includes('permission denied')) {
                setError('Erro de permissão. Verifique se o schema foi aplicado corretamente.')
            } else if (err.message?.includes('relation') && err.message?.includes('does not exist')) {
                setError('Tabela não encontrada. Execute o script de schema no Supabase.')
            } else {
                setError(`Erro ao carregar construtoras: ${err.message || 'Erro desconhecido'}`)
            }
        } finally {
            setLoading(false)
        }
    }

    const loadEstadosBrasil = async () => {
        try {
            setLoadingEstados(true)
            const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
            if (!response.ok) {
                throw new Error(`Falha ao carregar estados: ${response.status}`)
            }
            const data = await response.json()
            const estadosMapeados = (data || []).map((estado: any) => ({
                sigla: estado.sigla,
                nome: estado.nome
            }))
            if (estadosMapeados.length > 0) {
                setEstados(estadosMapeados)
            }
        } catch (err) {
            console.error('Erro ao carregar estados do IBGE:', err)
            setEstados(ESTADOS_BRASILEIROS)
        } finally {
            setLoadingEstados(false)
        }
    }

    const loadCidadesPorEstado = async (uf: string) => {
        if (!uf) {
            setCidades([])
            return
        }
        try {
            setLoadingCidades(true)
            const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
            if (!response.ok) {
                throw new Error(`Falha ao carregar cidades: ${response.status}`)
            }
            const data = await response.json()
            const nomesCidades = (data || []).map((cidade: any) => cidade.nome)
            setCidades(nomesCidades)
        } catch (err) {
            console.error('Erro ao carregar cidades do IBGE:', err)
            setCidades([])
        } finally {
            setLoadingCidades(false)
        }
    }

    const handleEstadoChange = (uf: string) => {
        setFormData(prev => ({
            ...prev,
            estado: uf,
            cidade: ''
        }))
        if (uf) {
            loadCidadesPorEstado(uf)
        } else {
            setCidades([])
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        try {
            // Validar dados obrigatórios
            if (!formData.nome || formData.nome.trim() === '') {
                setError('Nome da construtora é obrigatório')
                return
            }

            // CNPJ será gerado automaticamente, não precisa validar se está vazio

            // Gerar CNPJ automaticamente se não estiver editando
            let cnpjFinal = formData.cnpj
            if (!editingConstrutora) {
                cnpjFinal = generateCNPJFromName(formData.nome.trim())
                if (!cnpjFinal) {
                    setError('Erro ao gerar CNPJ automaticamente')
                    return
                }
            }

            // Validar CNPJ (formato básico)
            const cnpjLimpo = cnpjFinal.replace(/\D/g, '')
            if (cnpjLimpo.length !== 14) {
                setError('CNPJ deve ter 14 dígitos')
                return
            }

            // Verificar se há erro de CNPJ duplicado
            if (cnpjError) {
                setError('CNPJ já cadastrado no sistema')
                return
            }

            // Buscar o corretor_id do usuário logado
            if (!user?.id) {
                setError('Usuário não autenticado')
                return
            }

            const { data: corretor, error: corretorError } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', user.id)
                .single()

            if (corretorError || !corretor) {
                setError('Erro ao buscar dados do corretor')
                return
            }

            if (editingConstrutora) {
                // Atualizar construtora existente
                const { error } = await supabase
                    .from('construtoras')
                    .update({
                        nome: formData.nome,
                        cnpj: cnpjFinal,
                        contato_responsavel: formData.contato || null,
                        estado: formData.estado || null,
                        cidade: formData.cidade || null
                    })
                    .eq('id', editingConstrutora.id)

                if (error) {
                    if (error.code === '23505') {
                        setError('CNPJ já cadastrado no sistema')
                    } else {
                        throw error
                    }
                    return
                }
            } else {
                // Verificar se CNPJ já existe antes de criar
                const { data: existingConstrutora, error: checkError } = await supabase
                    .from('construtoras')
                    .select('id, cnpj')
                    .eq('cnpj', formatCNPJ(cnpjLimpo))
                    .single()

                if (checkError && checkError.code !== 'PGRST116') {
                    console.error('Erro ao verificar CNPJ:', checkError)
                    setError('Erro ao verificar CNPJ. Tente novamente.')
                    return
                }

                if (existingConstrutora) {
                    setError('CNPJ já cadastrado no sistema')
                    return
                }

                // Inserir construtora diretamente
                const { data: construtoraData, error: criarError } = await supabase
                    .from('construtoras')
                    .insert({
                        nome: formData.nome.trim(),
                        cnpj: cnpjFinal,
                        contato_responsavel: formData.contato?.trim() || null,
                        estado: formData.estado?.trim() || null,
                        cidade: formData.cidade?.trim() || null,
                        corretor_id: corretor.id
                    })
                    .select('id')
                    .single()

                if (criarError) {
                    console.error('Erro ao criar construtora:', criarError)

                    // Mensagem de erro mais específica
                    if (criarError.message?.includes('autenticado')) {
                        setError('Sessão expirada. Faça logout e login novamente.')
                    } else if (criarError.message?.includes('obrigatório')) {
                        setError(criarError.message)
                    } else if (criarError.code === '23505') {
                        setError('CNPJ já cadastrado no sistema')
                    } else {
                        setError('Erro ao criar construtora: ' + criarError.message)
                    }
                    return
                }

                const construtoraId = construtoraData?.id

                console.log('Construtora criada com sucesso! ID:', construtoraId)
            }

            setIsModalOpen(false)
            setGlobalModalOpen(false)
            setEditingConstrutora(null)
            setFormData({ nome: '', cnpj: '', contato: '', estado: '', cidade: '' })
            loadConstrutoras()

            // Mostrar toast de sucesso
            if (editingConstrutora) {
                showSuccess('Construtora atualizada com sucesso!')
            } else {
                showSuccess('Construtora criada com sucesso!')
            }
        } catch (err: any) {
            if (err.code === '23505') {
                setError('CNPJ já cadastrado no sistema')
            } else {
                setError(err.message || 'Erro ao salvar construtora')
            }
        }
    }

    const handleEdit = (construtora: Construtora) => {
        setEditingConstrutora(construtora)
        setFormData({
            nome: construtora.nome,
            cnpj: construtora.cnpj,
            contato: construtora.contato || '',
            estado: construtora.estado || '',
            cidade: construtora.cidade || ''
        })
        if (construtora.estado) {
            loadCidadesPorEstado(construtora.estado)
        } else {
            setCidades([])
        }
        setIsViewMode(false)
        setIsModalOpen(true)
        setGlobalModalOpen(true)
    }

    const handleView = (construtora: Construtora) => {
        setEditingConstrutora(construtora)
        setFormData({
            nome: construtora.nome,
            cnpj: construtora.cnpj,
            contato: construtora.contato || '',
            estado: construtora.estado || '',
            cidade: construtora.cidade || ''
        })
        if (construtora.estado) {
            loadCidadesPorEstado(construtora.estado)
        } else {
            setCidades([])
        }
        setIsViewMode(true)
        setIsModalOpen(true)
        setGlobalModalOpen(true)
    }

    const handleDelete = async (id: string, nome: string) => {
        if (!confirm(`Tem certeza que deseja excluir a construtora "${nome}"? Todos os imóveis relacionados também serão excluídos.`)) return

        try {
            const { error } = await supabase
                .from('construtoras')
                .delete()
                .eq('id', id)

            if (error) throw error

            loadConstrutoras()
        } catch (err: any) {
            setError(err.message || 'Erro ao excluir construtora')
        }
    }

    const cidadeQuery = formData.cidade?.toLowerCase() ?? ''
    const cidadeSuggestions = (cidadeQuery
        ? cidades.filter(cidade =>
            cidade.toLowerCase().includes(cidadeQuery)
        )
        : cidades
    ).slice(0, 20)

    const openCreateModal = () => {
        setEditingConstrutora(null)
        setFormData({ nome: '', cnpj: '', contato: '', estado: '', cidade: '' })
        setError('')
        setCnpjError('')
        setCidades([])
        setIsViewMode(false)
        setIsModalOpen(true)
        setGlobalModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setGlobalModalOpen(false)
        setEditingConstrutora(null)
        setFormData({ nome: '', cnpj: '', contato: '', estado: '', cidade: '' })
        setError('')
        setCnpjError('')
        setCidades([])
        setIsViewMode(false)
    }

    // Funções auxiliares
    const formatCNPJ = (cnpj: string) => {
        return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }

    // Gerar CNPJ automático baseado no nome da construtora
    const generateCNPJFromName = (nome: string) => {
        if (!nome || nome.trim() === '') return ''

        // Criar um hash simples baseado no nome
        let hash = 0
        for (let i = 0; i < nome.length; i++) {
            const char = nome.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash // Convert to 32bit integer
        }

        // Converter para número positivo e usar como base para CNPJ
        const baseNumber = Math.abs(hash).toString().padStart(14, '0').slice(0, 14)

        // Formatar como CNPJ
        return formatCNPJ(baseNumber)
    }

    const checkCNPJExists = async (cnpj: string) => {
        if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) {
            setCnpjError('')
            return
        }

        try {
            const { data: existingConstrutora, error } = await supabase
                .from('construtoras')
                .select('id, cnpj, nome')
                .eq('cnpj', formatCNPJ(cnpj.replace(/\D/g, '')))
                .single()

            if (error && error.code !== 'PGRST116') {
                console.error('Erro ao verificar CNPJ:', error)
                return
            }

            if (existingConstrutora) {
                setCnpjError(`CNPJ já cadastrado para: ${existingConstrutora.nome}`)
            } else {
                setCnpjError('')
            }
        } catch (err) {
            console.error('Erro ao verificar CNPJ:', err)
        }
    }

    const handleCNPJChange = (value: string) => {
        const cnpjLimpo = value.replace(/\D/g, '')
        const cnpjFormatado = formatCNPJ(cnpjLimpo)
        setFormData({ ...formData, cnpj: cnpjFormatado })

        // Verificar se CNPJ já existe (com delay para evitar muitas consultas)
        if (cnpjLimpo.length === 14) {
            setTimeout(() => checkCNPJExists(cnpjLimpo), 500)
        } else {
            setCnpjError('')
        }
    }

    // Filtros
    const filteredConstrutoras = construtoras.filter(construtora => {
        const matchesSearch =
            construtora.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (construtora.contato && construtora.contato.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (construtora.estado && construtora.estado.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (construtora.cidade && construtora.cidade.toLowerCase().includes(searchTerm.toLowerCase()))

        return matchesSearch
    })


    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('pt-BR')
    }

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center dark:bg-gray-900 min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">Carregando construtoras...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="p-4 lg:p-6 dark:bg-gray-900 min-h-screen">
            {/* Conteúdo principal com blur quando modal aberta */}
            <div className={`transition-all duration-300 ${isModalOpen ? 'blur-sm opacity-50' : ''}`}>
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Minhas Construtoras</h1>
                            <p className="text-gray-500 dark:text-gray-400">Gerencie suas construtoras pessoais</p>
                        </div>
                        <Button
                            variant="btn_add"
                            onClick={openCreateModal}
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Nova Construtora
                        </Button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Total de Construtoras</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{construtoras.length}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                    <Building className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Total de Imóveis</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {construtoras.reduce((acc, construtora) => acc + (construtora.imoveis_count || 0), 0)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hidden">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                    <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Valor Total Portfolio</p>
                                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {formatCurrency(construtoras.reduce((acc, construtora) => acc + (construtora.valor_total_imoveis || 0), 0))}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Buscar nas suas construtoras (nome, contato, estado, cidade)..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                />
                            </div>
                        </div>
                        {/* Toggle de Visualização */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('cards')}
                                    className={`p-2 rounded-md transition-colors ${viewMode === 'cards'
                                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                    title="Visualização em Cards"
                                >
                                    <Grid3x3 size={20} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                    title="Visualização em Lista"
                                >
                                    <List size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <p className="text-red-700 dark:text-red-400">{error}</p>
                            <button
                                onClick={() => setError('')}
                                className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Visualização de Construtoras */}
                {viewMode === 'cards' ? (
                    <ConstrutorasCardView
                        construtoras={filteredConstrutoras}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        formatDate={formatDate}
                    />
                ) : (
                    <ConstrutorasListView
                        construtoras={filteredConstrutoras}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        formatDate={formatDate}
                        searchTerm={searchTerm}
                    />
                )}
            </div>

            {/* Modal de Criar/Editar/Visualizar */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                {isViewMode ? 'Visualizar Construtora' : editingConstrutora ? 'Editar Construtora' : 'Nova Construtora'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <p className="text-red-700 dark:text-red-400">{error}</p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-6">
                                {/* Nome da Construtora */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Nome da Construtora *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        placeholder="Ex: Construtora ABC Ltda"
                                        required
                                        disabled={isViewMode}
                                    />
                                </div>

                                {/* CNPJ - Campo oculto com valor automático */}
                                <div className="hidden">
                                    <input
                                        type="text"
                                        value={formData.cnpj}
                                        onChange={(e) => handleCNPJChange(e.target.value)}
                                        className="hidden"
                                    />
                                </div>

                                {/* Contato */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Informações de Contato
                                    </label>
                                    <textarea
                                        value={formData.contato}
                                        onChange={(e) => setFormData({ ...formData, contato: e.target.value })}
                                        className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                        placeholder="Ex: contato@construtora.com.br | (11) 9999-9999 | Responsável: João Silva"
                                        rows={3}
                                        disabled={isViewMode}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">E-mail, telefone, responsável, etc.</p>
                                </div>

                                {/* Estado e Cidade */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Estado */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Estado
                                        </label>
                                        <select
                                            value={formData.estado}
                                            onChange={(e) => handleEstadoChange(e.target.value)}
                                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            disabled={isViewMode}
                                        >
                                            <option value="">Selecione o estado</option>
                                            {loadingEstados ? (
                                                <option value="" disabled>Carregando estados...</option>
                                            ) : (
                                                estados.map((estado) => (
                                                    <option key={estado.sigla} value={estado.sigla}>
                                                        {estado.sigla} - {estado.nome}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>

                                    {/* Cidade */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Cidade
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.cidade}
                                            onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                                            list="cidades-construtoras"
                                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                            placeholder="Ex: São Paulo"
                                            disabled={isViewMode || !formData.estado}
                                        />
                                        {!isViewMode && (
                                            <datalist id="cidades-construtoras">
                                                {cidadeSuggestions.map((cidade) => (
                                                    <option key={cidade} value={cidade} />
                                                ))}
                                            </datalist>
                                        )}
                                        {loadingCidades && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Carregando cidades...</p>
                                        )}
                                    </div>
                                </div>

                                {/* Informações adicionais no modo visualização */}
                                {isViewMode && editingConstrutora && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="text-center">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Total de Imóveis</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{editingConstrutora.imoveis_count || 0}</p>
                                        </div>
                                        <div className="text-center hidden">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Valor Total Portfolio</p>
                                            <p className="text-lg font-bold text-green-600 dark:text-green-400">
                                                {formatCurrency(editingConstrutora.valor_total_imoveis || 0)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isViewMode && (
                                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="btn-secondary"
                                    >
                                        Cancelar
                                    </button>
                                    <Button
                                        type="submit"
                                        variant="btn_add"
                                        className="flex items-center gap-2"
                                    >
                                        <Save size={20} />
                                        {editingConstrutora ? 'Atualizar' : 'Criar'} Construtora
                                    </Button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    )
}
